import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { attendanceAPI, benefitsAPI, employeeAPI, leaveAPI, payrollAPI } from '../services/api';
import {
  Bell,
  CheckSquare,
  Clock3,
  Loader2,
  RefreshCw,
  Send,
  Sparkles,
  SquareArrowOutUpRight,
} from 'lucide-react';
import { useTableControls } from '../hooks/useTableControls';
import { TableControls } from './ui/table-controls';

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatRelativeTime = (value) => {
  const date = parseDate(value);
  if (!date) return 'N/A';
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const EmployeeWorkspaceHub = ({ employeeId, onNavigateTab }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [records, setRecords] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [shortLeaves, setShortLeaves] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [profile, setProfile] = useState(null);
  const [taskState, setTaskState] = useState({});
  const [lastSeenAt, setLastSeenAt] = useState(0);

  const storageKey = `workspace-checklist-${employeeId || 'employee'}`;
  const notifKey = `workspace-notif-last-seen-${employeeId || 'employee'}`;

  const fetchWorkspaceData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const [recordsRes, leavesRes, shortLeavesRes, reimbursementsRes, benefitsRes, profileRes] =
        await Promise.all([
          attendanceAPI.getMyRecords(),
          leaveAPI.getMyLeaves(),
          leaveAPI.listShortLeaves(),
          payrollAPI.getReimbursements(),
          benefitsAPI.getMyBenefits(),
          employeeAPI.getMyProfile(),
        ]);

      setRecords(Array.isArray(recordsRes?.data) ? recordsRes.data : []);
      setLeaves(Array.isArray(leavesRes?.data) ? leavesRes.data : []);
      setShortLeaves(Array.isArray(shortLeavesRes?.data) ? shortLeavesRes.data : []);
      setReimbursements(Array.isArray(reimbursementsRes?.data) ? reimbursementsRes.data : []);
      setBenefits(Array.isArray(benefitsRes?.data) ? benefitsRes.data : []);
      setProfile(profileRes?.data || null);
    } catch (error) {
      console.error('Failed to fetch workspace data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchWorkspaceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        setTaskState(JSON.parse(saved));
      }
      const seen = localStorage.getItem(notifKey);
      if (seen) {
        setLastSeenAt(Number(seen));
      }
    } catch (error) {
      console.error('Failed to load workspace preferences:', error);
    }
  }, [storageKey, notifKey]);

  const saveTaskState = (next) => {
    setTaskState(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const todayRecord = useMemo(() => {
    const today = new Date().toDateString();
    return records.find((entry) => {
      const date = parseDate(entry.check_in_time || entry.date);
      return date && date.toDateString() === today;
    });
  }, [records]);

  const pendingLeaves = useMemo(
    () => leaves.filter((item) => String(item.status || '').toLowerCase() === 'pending'),
    [leaves]
  );

  const pendingShortLeaves = useMemo(
    () => shortLeaves.filter((item) => String(item.status || '').toLowerCase() === 'pending'),
    [shortLeaves]
  );

  const pendingReimbursements = useMemo(
    () => reimbursements.filter((item) => String(item.status || '').toLowerCase() === 'pending'),
    [reimbursements]
  );

  const pendingActions = useMemo(() => {
    const actions = [];
    if (!todayRecord?.check_in_time) {
      actions.push({
        id: 'checkin',
        title: 'Mark today check-in',
        detail: 'Attendance not marked for today yet.',
        cta: 'Open Attendance',
        tab: 'attendance',
      });
    } else if (!todayRecord?.check_out_time) {
      actions.push({
        id: 'checkout',
        title: 'Mark today check-out',
        detail: 'You are checked in; check-out is still pending.',
        cta: 'Complete Attendance',
        tab: 'attendance',
      });
    }
    if (pendingLeaves.length > 0) {
      actions.push({
        id: 'leave-followup',
        title: `${pendingLeaves.length} leave request(s) pending approval`,
        detail: 'Track SLA and follow up with approver if required.',
        cta: 'Open Leave History',
        tab: 'leave-history',
      });
    }
    if (pendingReimbursements.length > 0) {
      actions.push({
        id: 'expense-followup',
        title: `${pendingReimbursements.length} expense claim(s) pending`,
        detail: 'Add receipts/notes if approver requested more details.',
        cta: 'Open Expenses',
        tab: 'finance-reimbursements',
      });
    }
    if (!profile?.phone || !profile?.address) {
      actions.push({
        id: 'profile-complete',
        title: 'Complete your profile details',
        detail: 'Phone and address are required for smoother HR operations.',
        cta: 'Open Profile',
        tab: 'profile',
      });
    }
    return actions;
  }, [todayRecord, pendingLeaves.length, pendingReimbursements.length, profile?.phone, profile?.address]);

  const checklist = useMemo(
    () => [
      { id: 'attendance_done', label: 'Mark attendance for today' },
      { id: 'profile_complete', label: 'Keep profile details updated' },
      { id: 'expenses_reviewed', label: 'Review expense claims status' },
      { id: 'leave_balances_checked', label: 'Check leave balances this week' },
    ],
    []
  );

  const slaItems = useMemo(() => {
    const toItem = (entry, type, createdAt, tab, hours) => {
      const createdDate = parseDate(createdAt);
      if (!createdDate) return null;
      const dueAt = new Date(createdDate.getTime() + hours * 60 * 60 * 1000);
      const now = Date.now();
      const remaining = dueAt.getTime() - now;
      const totalWindow = dueAt.getTime() - createdDate.getTime();
      const elapsed = now - createdDate.getTime();
      const progress = totalWindow > 0 ? Math.min(Math.max(elapsed / totalWindow, 0), 1) : 1;
      const state = remaining <= 0 ? 'breached' : remaining <= totalWindow * 0.25 ? 'at_risk' : 'on_track';
      const remainingHours = Math.max(Math.floor(remaining / (1000 * 60 * 60)), 0);

      return {
        id: `${type}-${entry.id}`,
        type,
        reference: entry.leave_type || entry.claim_type || entry.reason || 'request',
        state,
        progress,
        remainingLabel: remaining <= 0 ? 'Breached' : `${remainingHours}h left`,
        createdAt: createdDate.toISOString(),
        tab,
      };
    };

    const leaveRows = pendingLeaves
      .map((entry) => toItem(entry, 'Leave Approval', entry.created_at || entry.requested_at || entry.start_date, 'leave-history', 48))
      .filter(Boolean);
    const shortLeaveRows = pendingShortLeaves
      .map((entry) => toItem(entry, 'Short Leave', entry.created_at || entry.requested_at || entry.date, 'attendance', 24))
      .filter(Boolean);
    const reimbursementRows = pendingReimbursements
      .map((entry) => toItem(entry, 'Expense Claim', entry.created_at || entry.claim_date, 'finance-reimbursements', 72))
      .filter(Boolean);

    return [...leaveRows, ...shortLeaveRows, ...reimbursementRows].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [pendingLeaves, pendingShortLeaves, pendingReimbursements]);
  const slaItemsTable = useTableControls(slaItems, {
    initialSortKey: 'createdAt',
    initialSortDirection: 'asc',
  });

  const notifications = useMemo(() => {
    const list = [];
    records.slice(0, 3).forEach((record) => {
      list.push({
        id: `attendance-${record.id}`,
        title: `Attendance ${record.status || 'updated'}`,
        detail: record.check_in_time ? `Check-in ${formatRelativeTime(record.check_in_time)}` : 'Attendance log updated',
        timestamp: parseDate(record.check_in_time || record.date)?.getTime() || 0,
      });
    });
    leaves.slice(0, 4).forEach((leave) => {
      list.push({
        id: `leave-${leave.id}`,
        title: `Leave ${leave.status || 'submitted'}`,
        detail: `${leave.leave_type || 'Leave'} request`,
        timestamp: parseDate(leave.updated_at || leave.created_at || leave.start_date)?.getTime() || 0,
      });
    });
    reimbursements.slice(0, 4).forEach((claim) => {
      list.push({
        id: `expense-${claim.id}`,
        title: `Expense ${claim.status || 'submitted'}`,
        detail: `${claim.claim_type || 'claim'} • ₹${Number(claim.amount || 0).toLocaleString()}`,
        timestamp: parseDate(claim.updated_at || claim.created_at || claim.claim_date)?.getTime() || 0,
      });
    });
    benefits.slice(0, 3).forEach((benefit) => {
      list.push({
        id: `benefit-${benefit.id}`,
        title: `Benefit ${benefit.status || 'active'}`,
        detail: benefit.plan?.name || 'Benefit update',
        timestamp: parseDate(benefit.updated_at || benefit.start_date)?.getTime() || 0,
      });
    });
    return list.sort((a, b) => b.timestamp - a.timestamp).slice(0, 12);
  }, [records, leaves, reimbursements, benefits]);

  const unreadNotifications = notifications.filter((item) => item.timestamp > lastSeenAt).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-hr-h1 font-bold tracking-tight">My Workspace</h1>
          <p className="text-hr-sm text-muted-foreground">
            Unified employee hub for actions, SLAs, shortcuts, and notifications.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchWorkspaceData(true)}
          disabled={refreshing}
        >
          {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2"><CardTitle className="text-hr-sm font-medium">Pending Actions</CardTitle></CardHeader>
          <CardContent><div className="text-hr-h2 font-bold">{pendingActions.length}</div></CardContent>
        </Card>
        <Card className="border-[hsl(var(--chart-2))]/20 bg-[hsl(var(--chart-2))]/10">
          <CardHeader className="pb-2"><CardTitle className="text-hr-sm font-medium">Approvals Awaiting</CardTitle></CardHeader>
          <CardContent><div className="text-hr-h2 font-bold">{pendingLeaves.length + pendingShortLeaves.length}</div></CardContent>
        </Card>
        <Card className="border-[hsl(var(--chart-4))]/20 bg-[hsl(var(--chart-4))]/10">
          <CardHeader className="pb-2"><CardTitle className="text-hr-sm font-medium">SLA Tracked</CardTitle></CardHeader>
          <CardContent><div className="text-hr-h2 font-bold">{slaItems.length}</div></CardContent>
        </Card>
        <Card className="border-[hsl(var(--chart-5))]/20 bg-[hsl(var(--chart-5))]/10">
          <CardHeader className="pb-2"><CardTitle className="text-hr-sm font-medium">Unread Notifications</CardTitle></CardHeader>
          <CardContent><div className="text-hr-h2 font-bold">{unreadNotifications}</div></CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2 border-border shadow-sm">
          <CardHeader>
            <CardTitle className="text-hr-lg">Pending Actions & Service Shortcuts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingActions.length === 0 ? (
              <div className="rounded-md border border-dashed p-4 text-hr-sm text-muted-foreground">
                No pending actions. You are up to date.
              </div>
            ) : (
              pendingActions.map((action) => (
                <div key={action.id} className="rounded-md border p-3 flex items-center justify-between gap-3 hover:bg-muted/40">
                  <div>
                    <p className="text-hr-sm font-medium">{action.title}</p>
                    <p className="text-hr-xs text-muted-foreground">{action.detail}</p>
                  </div>
                  <Button size="sm" onClick={() => onNavigateTab?.(action.tab)}>
                    {action.cta}
                    <SquareArrowOutUpRight className="h-3.5 w-3.5 ml-2" />
                  </Button>
                </div>
              ))
            )}

            <div className="pt-2">
              <p className="text-hr-sm font-medium mb-2">Service Shortcuts</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => onNavigateTab?.('attendance')}>Attendance</Button>
                <Button size="sm" variant="outline" onClick={() => onNavigateTab?.('leave-apply')}>Apply Leave</Button>
                <Button size="sm" variant="outline" onClick={() => onNavigateTab?.('leave-history')}>Leave History</Button>
                <Button size="sm" variant="outline" onClick={() => onNavigateTab?.('finance-reimbursements')}>Expenses</Button>
                <Button size="sm" variant="outline" onClick={() => onNavigateTab?.('finance-payslips')}>Payslips</Button>
                <Button size="sm" variant="outline" onClick={() => onNavigateTab?.('benefits-my')}>My Benefits</Button>
                <Button size="sm" variant="outline" onClick={() => onNavigateTab?.('profile')}>Profile</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-hr-lg flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" />
              Notification Center
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const now = Date.now();
                setLastSeenAt(now);
                localStorage.setItem(notifKey, String(now));
              }}
            >
              Mark Read
            </Button>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[360px] overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="text-hr-sm text-muted-foreground">No notifications yet.</p>
            ) : (
              notifications.map((item) => (
                <div key={item.id} className="rounded-md border p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-hr-sm font-medium">{item.title}</p>
                    {item.timestamp > lastSeenAt ? <Badge variant="info">New</Badge> : null}
                  </div>
                  <p className="text-hr-xs text-muted-foreground mt-1">{item.detail}</p>
                  <p className="text-hr-xs text-muted-foreground mt-1">{formatRelativeTime(item.timestamp)}</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="text-hr-lg flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-primary" />
              Self-Service SLA Queue
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-hr-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">Request</th>
                    <th className="text-left p-3">SLA State</th>
                    <th className="text-left p-3">Countdown</th>
                    <th className="text-left p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {slaItemsTable.data.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="p-4 text-center text-muted-foreground text-hr-sm">
                        No active SLA tracked requests.
                      </td>
                    </tr>
                  ) : (
                    slaItemsTable.data.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-muted/40">
                        <td className="p-3">
                          <p className="font-medium">{item.type}</p>
                          <p className="text-hr-xs text-muted-foreground">{item.reference}</p>
                        </td>
                        <td className="p-3">
                          <Badge
                            variant={
                              item.state === 'breached'
                                ? 'danger'
                                : item.state === 'at_risk'
                                  ? 'warning'
                                  : 'success'
                            }
                          >
                            {item.state.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="p-3">{item.remainingLabel}</td>
                        <td className="p-3">
                          <Button size="sm" variant="outline" onClick={() => onNavigateTab?.(item.tab)}>
                            Open
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="pt-3">
              <TableControls
                totalRows={slaItemsTable.totalRows}
                page={slaItemsTable.page}
                totalPages={slaItemsTable.totalPages}
                pageSize={slaItemsTable.pageSize}
                pageSizes={slaItemsTable.pageSizes}
                onPageSizeChange={slaItemsTable.onPageSizeChange}
                onPrevious={slaItemsTable.goToPreviousPage}
                onNext={slaItemsTable.goToNextPage}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="text-hr-lg flex items-center gap-2">
              <CheckSquare className="h-4 w-4 text-primary" />
              Task Checklist
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {checklist.map((task) => (
              <label key={task.id} className="flex items-center gap-3 rounded-md border p-3 hover:bg-muted/40">
                <Checkbox
                  checked={Boolean(taskState[task.id])}
                  onCheckedChange={(checked) =>
                    saveTaskState({
                      ...taskState,
                      [task.id]: Boolean(checked),
                    })
                  }
                />
                <span className="text-hr-sm">{task.label}</span>
              </label>
            ))}
            <div className="rounded-md border border-dashed p-3 text-hr-xs text-muted-foreground flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" />
              Checklist progress is stored per employee on this device.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmployeeWorkspaceHub;
